import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react'
import localStorageService from 'services/storage'
import currencyCache from 'services/currencyCache'
import { Currency, CurrencyExchange } from 'services/currencyApi'

interface CurrencyContextType {
  currentCurrency: string
  setCurrentCurrency: (currency: string) => void
  currencies: Currency[]
  exchangeRates: Record<string, CurrencyExchange>
  isLoading: boolean
  isLoadingExchangeRates: boolean
  error: string | null
  refreshCurrencies: () => Promise<void>
  getExchangeRates: (currencyCode: string, startYear?: number, endYear?: number) => Promise<CurrencyExchange | null>
  convertCurrency: (amount: number, fromCurrency: string, toCurrency: string, year?: number) => Promise<number | null>
  preloadCurrencyData: (currencyCode: string) => Promise<void>
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined)

interface CurrencyProviderProps {
  children: ReactNode
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currentCurrency, setCurrentCurrency] = useState(() => localStorageService.currentCurrency)
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [exchangeRates, setExchangeRates] = useState<Record<string, CurrencyExchange>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingExchangeRates, setIsLoadingExchangeRates] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ongoingRequests = useRef<Map<string, Promise<any>>>(new Map())

  const getRequestKey = (currencyCode: string, startYear?: number, endYear?: number): string => {
    return `exchange_rates_${currencyCode}_${startYear || 'all'}_${endYear || 'all'}`
  }

  const getAllYearlyRequestKey = (currencyCode: string): string => {
    return `all_yearly_${currencyCode}`
  }

  useEffect(() => {
    localStorageService.currentCurrency = currentCurrency
  }, [currentCurrency])

  useEffect(() => {
    loadCurrencies()
  }, [])

  useEffect(() => {
    if (currentCurrency && currentCurrency !== 'USD') {
      preloadCurrencyData(currentCurrency)
    }
  }, [currentCurrency])

  const loadCurrencies = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const fetchedCurrencies = await currencyCache.getCurrencies()
      setCurrencies(fetchedCurrencies)

      if (fetchedCurrencies.length === 0) {
        setError('No currencies available. Please check your connection and try again.')
      }
    } catch (err) {
      setError('Failed to load currencies')
      console.error('Error loading currencies:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshCurrencies = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const refreshedCurrencies = await currencyCache.refreshCurrencies()
      setCurrencies(refreshedCurrencies)
    } catch (err) {
      setError('Failed to refresh currencies')
      console.error('Error refreshing currencies:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const preloadCurrencyData = useCallback(
    async (currencyCode: string) => {
      try {
        const cacheKey = `${currencyCode}_all_yearly`
        if (exchangeRates[cacheKey] && exchangeRates[cacheKey].values) {
          return
        }

        const requestKey = getAllYearlyRequestKey(currencyCode)
        if (ongoingRequests.current.has(requestKey)) {
          setIsLoadingExchangeRates(true)
          await ongoingRequests.current.get(requestKey)
          setIsLoadingExchangeRates(false)
          return
        }

        setIsLoadingExchangeRates(true)
        const requestPromise = currencyCache.getAllYearlyExchangeRates(currencyCode)
        ongoingRequests.current.set(requestKey, requestPromise)

        try {
          const rates = await requestPromise
          if (rates) {
            setExchangeRates(prev => ({
              ...prev,
              [cacheKey]: rates,
            }))
          }
        } finally {
          ongoingRequests.current.delete(requestKey)
          setIsLoadingExchangeRates(false)
        }
      } catch (err) {
        console.error(`Error pre-loading exchange data for ${currencyCode}:`, err)
        setIsLoadingExchangeRates(false)
      }
    },
    [exchangeRates],
  )

  const getExchangeRates = useCallback(
    async (currencyCode: string, startYear?: number, endYear?: number): Promise<CurrencyExchange | null> => {
      try {
        const requestKey = getRequestKey(currencyCode, startYear, endYear)
        if (ongoingRequests.current.has(requestKey)) {
          return await ongoingRequests.current.get(requestKey)
        }

        const requestPromise = (async () => {
          const rates = await currencyCache.getExchangeRates(currencyCode, startYear, endYear)
          if (rates) {
            const cacheKey = `${currencyCode}_${startYear || 'all'}_${endYear || 'all'}`
            setExchangeRates(prev => ({
              ...prev,
              [cacheKey]: rates,
            }))
          }
          return rates
        })()

        ongoingRequests.current.set(requestKey, requestPromise)

        try {
          return await requestPromise
        } finally {
          ongoingRequests.current.delete(requestKey)
        }
      } catch (err) {
        console.error(`Error fetching exchange rates for ${currencyCode}:`, err)
        return null
      }
    },
    [],
  )

  const convertCurrency = useCallback(
    async (amount: number, fromCurrency: string, toCurrency: string, year?: number): Promise<number | null> => {
      if (fromCurrency === toCurrency) return amount

      try {
        const cacheKey = `${toCurrency}_all_yearly`
        let rates = exchangeRates[cacheKey]

        if (!rates || !rates.values) {
          const requestKey = getAllYearlyRequestKey(toCurrency)
          if (ongoingRequests.current.has(requestKey)) {
            const fetchedRates = await ongoingRequests.current.get(requestKey)
            if (!fetchedRates) return null
            rates = fetchedRates
          } else {
            const requestPromise = currencyCache.getAllYearlyExchangeRates(toCurrency)
            ongoingRequests.current.set(requestKey, requestPromise)

            try {
              const fetchedRates = await requestPromise
              if (!fetchedRates) return null
              rates = fetchedRates

              setExchangeRates(prev => ({
                ...prev,
                [cacheKey]: rates,
              }))
            } finally {
              ongoingRequests.current.delete(requestKey)
            }
          }
        } else {
          console.log(`Using cached exchange rates for ${toCurrency}`)
        }

        let rate = 1
        if (year && rates.values[year.toString()]) {
          rate = rates.values[year.toString()]
        } else {
          const years = Object.keys(rates.values)
            .map(Number)
            .sort((a, b) => b - a)
          if (years.length > 0) {
            rate = rates.values[years[0].toString()]
          }
        }

        return amount * rate
      } catch (error) {
        console.error('Error converting currency:', error)
        return null
      }
    },
    [exchangeRates],
  )

  const value: CurrencyContextType = {
    currentCurrency,
    setCurrentCurrency,
    currencies,
    exchangeRates,
    isLoading,
    isLoadingExchangeRates,
    error,
    refreshCurrencies,
    getExchangeRates,
    convertCurrency,
    preloadCurrencyData,
  }

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext)
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}
